import React, { forwardRef, useMemo } from 'react'
import cx from 'classnames'
import { useAppSelector } from 'reduxTyped'
import { Icon } from '@manychat/manyui'

import { getBotTimezone } from 'common/core/selectors/appSelectors'
import { getLocalCaptionFor } from 'common/fields/api/localCaptions'
import { FieldType } from 'common/fields/entities/enums'
import { getAllFields } from 'common/fields/linking/accountAvailableFields'
import { FieldTypeIcons } from 'common/fields/linking/hooks/useFieldsOptions'
import { formatDateTime } from 'common/usesList/utils'

import cm from './styles.module.css'

interface FieldObtainedMessageProps {
  fieldId: string
  value: string | number | boolean
}

export const FieldObtainedMessage = forwardRef<HTMLLIElement, FieldObtainedMessageProps>(
  ({ fieldId, value }: FieldObtainedMessageProps, ref) => {
    const field = useAppSelector((state) => {
      const allFields = getAllFields(state)

      if (!allFields) {
        return null
      }

      return allFields[fieldId]
    })

    const botTimezone = useAppSelector(getBotTimezone)

    const IconComponent = useMemo(() => {
      if (!field) {
        return Icon.CufNoIcon
      }

      return field.type === FieldType.SYSTEM_TEXT
        ? FieldTypeIcons[field.id]
        : FieldTypeIcons[field.type]
    }, [field])

    const processedValue = useMemo(() => {
      if (!field) {
        return value
      }

      switch (field.type) {
        case FieldType.DATE:
          return formatDateTime(value, true, botTimezone)
        case FieldType.DATETIME:
          return formatDateTime(value, false, botTimezone)
        case FieldType.BOOLEAN:
          return value.toString()
        default:
          return value
      }
    }, [field, value, botTimezone])

    if (!field) {
      return null
    }

    return (
      <li className={cm.wrapper} ref={ref}>
        <div className={cx('text-ellipsis', cm.message)}>
          <IconComponent className="flex-shrink-0" size={16} />

          <span className="text-ellipsis font-semibold">
            {getLocalCaptionFor(field.caption) || field.caption}:
          </span>

          <span className="flex-shrink-0 text-ellipsis">{processedValue}</span>
        </div>
      </li>
    )
  },
)
